import styled, { css } from 'styled-components';

export const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

export const Card = styled.section`
  background: #ffffff;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  overflow: hidden;
`;

export const SectionHeader = styled.header`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 22px;
  border-bottom: 1px solid #eef2f7;
  background: #fbfcfe;

  h3 {
    margin: 0;
    font-size: 13px;
    font-weight: 700;
    color: #334155;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  svg {
    color: #006633;
  }
`;

export const SectionBody = styled.div`
  padding: 20px 22px;
`;

/* Header card (profile identity) */
export const ProfileHeader = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 20px;
  padding: 22px;

  @media (max-width: 768px) {
    grid-template-columns: auto 1fr;
    .header-actions {
      grid-column: 1 / -1;
      justify-content: flex-start;
    }
  }
`;

export const Avatar = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(135deg, #006633 0%, #00874a 100%);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.02em;
  box-shadow: 0 4px 12px rgba(0, 102, 51, 0.18);
  flex-shrink: 0;
`;

export const Identity = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;

  h1 {
    margin: 0;
    font-size: 22px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.03em;
    line-height: 1.15;
  }

  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 14px;
    font-size: 13px;
    color: #64748b;

    span {
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }

    svg {
      color: #94a3b8;
    }
  }
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;

  @media print {
    display: none !important;
  }
`;

const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  font-family: inherit;
  white-space: nowrap;
`;

export const GhostButton = styled.button`
  ${buttonBase}
  background: #ffffff;
  color: #334155;
  border: 1px solid #e2e8f0;

  &:hover {
    background: #f8fafc;
    border-color: #006633;
    color: #006633;
  }
`;

export const PrimaryButton = styled.button`
  ${buttonBase}
  background: #006633;
  color: #ffffff;
  border: 1px solid transparent;

  &:hover {
    background: #00451f;
  }
`;

export const DangerButton = styled.button`
  ${buttonBase}
  background: #ffffff;
  color: #b91c1c;
  border: 1px solid #fecaca;

  &:hover {
    background: #fef2f2;
    border-color: #b91c1c;
  }
`;

export const IconOnly = styled.button`
  ${buttonBase}
  width: 36px;
  height: 36px;
  padding: 0;
  background: #ffffff;
  color: #334155;
  border: 1px solid #e2e8f0;

  &:hover {
    background: #f8fafc;
    border-color: #006633;
    color: #006633;
  }
`;

/* Status banners */
type BannerTone = 'success' | 'warning' | 'info' | 'neutral';
const BANNER_MAP: Record<
  BannerTone,
  {
    bg: string;
    border: string;
    iconBg: string;
    iconColor: string;
    title: string;
  }
> = {
  success: {
    bg: '#ecfdf5',
    border: '#bbf7d0',
    iconBg: '#dcfce7',
    iconColor: '#006633',
    title: '#065f46',
  },
  warning: {
    bg: '#fffbeb',
    border: '#fde68a',
    iconBg: '#fef3c7',
    iconColor: '#b45309',
    title: '#92400e',
  },
  info: {
    bg: '#eff6ff',
    border: '#bfdbfe',
    iconBg: '#dbeafe',
    iconColor: '#1d4ed8',
    title: '#1e3a8a',
  },
  neutral: {
    bg: '#f8fafc',
    border: '#e2e8f0',
    iconBg: '#f1f5f9',
    iconColor: '#475569',
    title: '#334155',
  },
};

export const Banner = styled.div<{ $tone: BannerTone }>`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  border-radius: 14px;

  ${({ $tone }) => css`
    background: ${BANNER_MAP[$tone].bg};
    border: 1px solid ${BANNER_MAP[$tone].border};
  `}

  .icon {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    ${({ $tone }) => css`
      background: ${BANNER_MAP[$tone].iconBg};
      color: ${BANNER_MAP[$tone].iconColor};
    `}
  }

  .content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .title {
    font-size: 14px;
    font-weight: 700;
    ${({ $tone }) => css`
      color: ${BANNER_MAP[$tone].title};
    `}
  }

  .desc {
    font-size: 13px;
    color: #475569;
    line-height: 1.4;
  }

  .actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    flex-wrap: wrap;
  }

  @media print {
    display: none;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;

    .actions {
      width: 100%;
      justify-content: flex-start;
    }
  }
`;

/* Chips for main identity */
type ChipTone = 'success' | 'warning' | 'neutral' | 'info';
const CHIP_MAP: Record<ChipTone, { bg: string; color: string }> = {
  success: { bg: '#e6f3ec', color: '#006633' },
  warning: { bg: '#fff4e0', color: '#b45309' },
  info: { bg: '#e0ecff', color: '#1d4ed8' },
  neutral: { bg: '#f1f5f9', color: '#475569' },
};

export const StatusChip = styled.span<{ $tone: ChipTone }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  ${({ $tone }) => css`
    background: ${CHIP_MAP[$tone].bg};
    color: ${CHIP_MAP[$tone].color};
  `}

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
`;

/* Grid of key/value */
export const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px 28px;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

export const Field = styled.div<{ $span?: number }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  grid-column: span ${({ $span }) => $span || 1};

  @media (max-width: 992px) {
    grid-column: span ${({ $span }) => Math.min($span || 1, 2)};
  }

  @media (max-width: 576px) {
    grid-column: span 1;
  }

  .label {
    font-size: 11px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  .value {
    font-size: 14px;
    color: #0f172a;
    font-weight: 500;
    min-height: 20px;
    word-break: break-word;

    &.empty {
      color: #cbd5e1;
      font-style: italic;
      font-weight: 400;
    }
  }
`;

export const ConsentList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

export const ConsentItem = styled.div<{ $agreed: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid ${({ $agreed }) => ($agreed ? '#bbf7d0' : '#fecaca')};
  background: ${({ $agreed }) => ($agreed ? '#ecfdf5' : '#fef2f2')};

  .icon {
    width: 32px;
    height: 32px;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    ${({ $agreed }) => css`
      background: ${$agreed ? '#dcfce7' : '#fee2e2'};
      color: ${$agreed ? '#006633' : '#b91c1c'};
    `}
  }

  .text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .title {
    font-size: 13px;
    font-weight: 600;
    color: #0f172a;
  }

  .status {
    font-size: 12px;
    font-weight: 500;
    ${({ $agreed }) => css`
      color: ${$agreed ? '#006633' : '#b91c1c'};
    `}
  }
`;

/* Legacy export kept for backward compat if referenced elsewhere */
export const DataInfoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #334155;
`;
